// src/lib/request.js
import { getIdToken } from "./auth/getToken";
import { getAuth } from "firebase/auth";

// ---- Single source of truth ----
export const API_BASE = "https://go.switchbaq.com";
const SUPPORT_ACCOUNT_KEY = "sb_support_account_v1";

// ---- Optional 401 handler (e.g., sign out or redirect to /login) ----
let onUnauthorized = null;
export function setOnUnauthorized(fn) { onUnauthorized = fn; }

function getErrorMessage(data, status) {
  if (typeof data === "string" && data.trim()) return data;
  if (typeof data?.error === "string" && data.error.trim()) return data.error;
  if (typeof data?.error?.message === "string" && data.error.message.trim()) return data.error.message;
  if (typeof data?.message === "string" && data.message.trim()) return data.message;
  return `HTTP ${status}`;
}

function joinUrl(path) {
  return path.startsWith("http") ? path : `${API_BASE}${path}`;
}

async function buildHeaders(headers, isForm) {
  const token = await getIdToken();              // cached token by default
  const h = new Headers(headers || {});
  h.set("Accept", "application/json");
  if (!isForm && !h.has("Content-Type")) h.set("Content-Type", "application/json");
  if (token) h.set("Authorization", `Bearer ${token}`);  // sent on every call

  try {
    const supportAccount = JSON.parse(sessionStorage.getItem(SUPPORT_ACCOUNT_KEY) || "null");
    if (supportAccount?.id && !h.has("X-Acting-Account-Id")) {
      h.set("X-Acting-Account-Id", supportAccount.id);
    }
  } catch {}

  if (process.env.REACT_APP_X_DOMAIN && !h.has("x-domain")) {
    h.set("x-domain", process.env.REACT_APP_X_DOMAIN);
  }

  return h;
}

function isFormData(body) {
  return typeof FormData !== "undefined" && body instanceof FormData;
}

function makeAbortable(timeoutMs) {
  if (!timeoutMs) return {};
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  return {
    signal: ctrl.signal,
    done: () => clearTimeout(t),
  };
}

/** Core fetch with smart defaults (infers method if omitted) */
export async function apiFetch(path, init = {}) {
  const { method, headers, body, timeoutMs, ...rest } = init;
  const url = joinUrl(path);

  const form = isFormData(body);
  const finalHeaders = await buildHeaders(headers, form);
  const finalMethod = method || (body != null ? "POST" : "GET");
  const finalBody = form ? body : (body != null && typeof body !== "string" ? JSON.stringify(body) : body);

  const abortable = makeAbortable(timeoutMs);
  let res;
  try {
    res = await fetch(url, {
      method: finalMethod,
      headers: finalHeaders,
      body: finalBody,
      signal: abortable.signal,
      ...rest,
      credentials: "omit",
    });
  } finally {
    abortable.done && abortable.done();
  }

  const ct = res.headers.get("content-type") || "";
  const hasBody = res.status !== 204 && res.status !== 205;
  const data = hasBody
    ? (ct.includes("application/json")
        ? await res.json().catch(() => null)
        : await res.text())
    : null;

  if (!res.ok) {
    const err = new Error(getErrorMessage(data, res.status));
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

/** Retry once on 401 with a forced token refresh; call onUnauthorized if it still fails */
export async function apiFetchWithRetry(path, init = {}) {
  try {
    return await apiFetch(path, init);
  } catch (e) {
    if (e instanceof TypeError && /failed to fetch|networkerror|load failed/i.test(e.message || '')) {
      return apiFetch(path, init);
    }
    if (e.status !== 401) throw e;

    // Force-refresh the ID token, then retry
    try {
      const user = getAuth().currentUser;
      if (user) await user.getIdToken(true);
      return await apiFetch(path, init);
    } catch (e2) {
      if (e2.status === 401 && typeof onUnauthorized === "function") {
        try { onUnauthorized(e2); } catch {}
      }
      throw e2;
    }
  }
}

/** Convenience helpers (use retry by default so call sites stay clean) */
export const api = {
  get:   (path, opts)       => apiFetchWithRetry(path, { ...opts, method: "GET" }),
  post:  (path, body, opts) => apiFetchWithRetry(path, { ...opts, method: "POST",  body }),
  put:   (path, body, opts) => apiFetchWithRetry(path, { ...opts, method: "PUT",   body }),
  patch: (path, body, opts) => apiFetchWithRetry(path, { ...opts, method: "PATCH", body }),
  delete:(path, opts)       => apiFetchWithRetry(path, { ...opts, method: "DELETE" }),
};

/** Escape hatch: get the raw Response when you need streams/files */
export async function apiRaw(path, init = {}) {
  // raw version without JSON parsing; still attaches auth and supports timeout + 401 retry
  try {
    const { method, headers, body, timeoutMs, ...rest } = init;
    const url = joinUrl(path);
    const form = isFormData(body);
    const finalHeaders = await buildHeaders(headers, form);
    const finalMethod = method || (body != null ? "POST" : "GET");
    const finalBody = form ? body : (body != null && typeof body !== "string" ? JSON.stringify(body) : body);

    const abortable = makeAbortable(timeoutMs);
    let res;
    try {
      res = await fetch(url, {
        method: finalMethod,
        headers: finalHeaders,
        body: finalBody,
        signal: abortable.signal,
        ...rest,
        credentials: "omit",
      });
    } finally {
      abortable.done && abortable.done();
    }

    if (res.status === 401) {
      const user = getAuth().currentUser;
      if (user) await user.getIdToken(true);
      const res2 = await fetch(url, {
        method: finalMethod,
        headers: await buildHeaders(headers, form),
        body: finalBody,
        signal: abortable.signal,
        ...rest,
        credentials: "omit",
      });
      if (res2.status === 401 && typeof onUnauthorized === "function") {
        try { onUnauthorized(new Error("Unauthorized")); } catch {}
      }
      return res2;
    }
    return res;
  } catch (e) {
    throw e;
  }
}
